import React, { useState, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import { useNotification } from '../../context/NotificationContext';
import apiClient from '../../services/api';
import { getSignedUrl, uploadToS3 } from '../../services/upload';
import './UniversalProductLibrary.css';
import '../products/Products.css';

const API_BASE = process.env.REACT_APP_SERVER_DOMAIN;

// SVG Icons
const ArrowLeftIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
  </svg>
);

const ChevronUpIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="18 15 12 9 6 15"/>
  </svg>
);

const ChevronDownIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);

const VerifiedIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="#3b82f6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 12l2 2 4-4"/>
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="#3b82f6" stroke="#3b82f6"/>
    <path d="M9 12l2 2 4-4" fill="none" stroke="#fff"/>
  </svg>
);

const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);

const XIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const ScissorsIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="6" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><line x1="20" y1="4" x2="8.12" y2="15.88"/><line x1="14.47" y1="14.48" x2="20" y2="20"/><line x1="8.12" y1="8.12" x2="12" y2="12"/>
  </svg>
);

const SIZE_UNITS = ['g', 'kg', 'mg', 'oz', 'lb', 'mL', 'L', 'fl oz', 'gal', 'count', 'pack'];

export default function UniversalProductForm({ product, onClose, onSave, onDelete, onVerify, onPrev, onNext, categories, countries }) {
  const { getAccessTokenSilently } = useAuth0();
  const notification = useNotification();
  const [isSaving, setIsSaving] = useState(false);
  const closeAfterSaveRef = useRef(false);
  const isEditMode = !!product?._id;

  const [formData, setFormData] = useState({
    name: product?.name || '',
    brand: product?.brand || '',
    manufacturer: product?.manufacturer || '',
    category: product?.category || '',
    subcategory: product?.subcategory || '',
    department: product?.department || '',
    country: product?.country || 'CA',
    upc: product?.upc || '',
    ean: product?.ean || '',
    sku: product?.sku || '',
    size: product?.size || '',
    sizeValue: product?.sizeValue ?? '',
    sizeUnit: product?.sizeUnit || '',
    packSize: product?.packSize ?? '',
    msrp: product?.msrp ?? '',
    tags: product?.tags || [],
    aliases: product?.aliases || [],
    keywords: product?.keywords || [],
    images: product?.images || []
  });

  const [priceRangeMin, setPriceRangeMin] = useState(product?.typicalPriceRange?.min?.toString() || '');
  const [priceRangeMax, setPriceRangeMax] = useState(product?.typicalPriceRange?.max?.toString() || '');
  const [tagInput, setTagInput] = useState('');
  const [aliasInput, setAliasInput] = useState('');
  const [keywordInput, setKeywordInput] = useState('');
  const [imageUrlInput, setImageUrlInput] = useState('');
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [removingBgIndex, setRemovingBgIndex] = useState(null);
  const fileInputRef = useRef(null);

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    if (!formData.name || !formData.category || !formData.country) {
      notification.error('Please fill in all required fields (Name, Category, Country)');
      return;
    }

    setIsSaving(true);
    try {
      const token = await getAccessTokenSilently();
      const payload = {
        ...formData,
        sizeValue: formData.sizeValue !== '' ? parseFloat(formData.sizeValue) : undefined,
        packSize: formData.packSize !== '' ? parseInt(formData.packSize, 10) : undefined,
        msrp: formData.msrp !== '' ? parseFloat(formData.msrp) : undefined,
        typicalPriceRange: priceRangeMin && priceRangeMax ? {
          min: parseFloat(priceRangeMin),
          max: parseFloat(priceRangeMax)
        } : undefined
      };

      // Clean up empty string fields
      Object.keys(payload).forEach(key => {
        if (payload[key] === '') delete payload[key];
      });

      if (product?._id) {
        await axios.put(`${API_BASE}/api/universal-products/admin/${product._id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        notification.success('Product updated successfully');
      } else {
        await axios.post(`${API_BASE}/api/universal-products/admin/create`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        notification.success('Product created successfully');
      }
      if (closeAfterSaveRef.current) {
        closeAfterSaveRef.current = false;
        onClose();
      }
      onSave();
    } catch (error) {
      const msg = error.response?.data?.message || error.response?.data?.error || 'Failed to save product';
      notification.error(msg);
    } finally {
      setIsSaving(false);
    }
  };

  // Tag helpers
  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      updateField('tags', [...formData.tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    updateField('tags', formData.tags.filter(t => t !== tag));
  };

  const addAlias = () => {
    if (aliasInput.trim() && !formData.aliases.includes(aliasInput.trim())) {
      updateField('aliases', [...formData.aliases, aliasInput.trim()]);
      setAliasInput('');
    }
  };

  const removeAlias = (alias) => {
    updateField('aliases', formData.aliases.filter(a => a !== alias));
  };

  const addKeyword = () => {
    if (keywordInput.trim() && !formData.keywords.includes(keywordInput.trim())) {
      updateField('keywords', [...formData.keywords, keywordInput.trim()]);
      setKeywordInput('');
    }
  };

  const removeKeyword = (keyword) => {
    updateField('keywords', formData.keywords.filter(k => k !== keyword));
  };

  // Image helpers - re-uploads external URLs to S3 to avoid CORS issues
  const addImage = async () => {
    const url = imageUrlInput.trim();
    if (!url) return;

    setIsUploadingImage(true);
    try {
      // Re-upload to S3 via server proxy
      const response = await apiClient.post('/api/s3/upload-from-url', { url, type: 'product' });
      const s3Url = response.data.url;

      const newImage = {
        url: s3Url,
        isPrimary: formData.images.length === 0,
        source: 'manual'
      };
      updateField('images', [...formData.images, newImage]);
      setImageUrlInput('');
    } catch {
      notification.error('Failed to upload image. The URL may be inaccessible.');
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploadingImage(true);
    try {
      const signedUrl = await getSignedUrl('product', file.name);
      const s3Url = await uploadToS3(signedUrl, file);
      const newImage = {
        url: s3Url,
        isPrimary: formData.images.length === 0,
        source: 'manual'
      };
      updateField('images', [...formData.images, newImage]);
    } catch {
      notification.error('Failed to upload image');
    } finally {
      setIsUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeBackground = async (index) => {
    const image = formData.images[index];
    if (!image?.url) return;

    setRemovingBgIndex(index);
    try {
      const token = await getAccessTokenSilently();
      const response = await axios.post(
        `${API_BASE}/api/image-processing/remove-background-url`,
        { imageUrl: image.url, upload: true },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      if (response.data?.success) {
        const newImages = [...formData.images];
        newImages[index] = { ...newImages[index], url: response.data.imageUrl };
        updateField('images', newImages);
        notification.success('Background removed');
      } else {
        throw new Error(response.data?.error || 'Failed to remove background');
      }
    } catch (error) {
      notification.error(error.response?.data?.error || error.message || 'Failed to remove background');
    } finally {
      setRemovingBgIndex(null);
    }
  };

  const removeImage = (index) => {
    const newImages = [...formData.images];
    newImages.splice(index, 1);
    if (newImages.length > 0 && !newImages.some(img => img.isPrimary)) {
      newImages[0].isPrimary = true;
    }
    updateField('images', newImages);
  };

  const setPrimaryImage = (index) => {
    const newImages = formData.images.map((img, i) => ({
      ...img,
      isPrimary: i === index
    }));
    updateField('images', newImages);
  };

  return (
    <div className="add-product-container">
      {/* Toolbar */}
      <div className="add-product-toolbar">
        <div className="toolbar-left">
          <button className="upl-back-btn" onClick={onClose}>
            <ArrowLeftIcon />
          </button>
          {isEditMode && (
            <div className="upl-nav-arrows">
              <button className="upl-nav-btn" onClick={onPrev} disabled={!onPrev} title="Previous product">
                <ChevronUpIcon />
              </button>
              <button className="upl-nav-btn" onClick={onNext} disabled={!onNext} title="Next product">
                <ChevronDownIcon />
              </button>
            </div>
          )}
          <div className="toolbar-title">
            <h1>{product ? 'Edit Product' : 'Add New Product'}</h1>
            <p>{product ? 'Update product information' : 'Add a new product to the global catalog'}</p>
          </div>
        </div>
        {isEditMode && onVerify && (
          <button
            type="button"
            className={`upl-btn ${product.dataQuality?.isVerified ? 'verified-active' : 'outline'}`}
            onClick={onVerify}
          >
            <VerifiedIcon size={16} />
            {product.dataQuality?.isVerified ? 'Verified' : 'Verify'}
          </button>
        )}
      </div>

      {/* Scrollable Content */}
      <div className="add-product-content">
        <div className="upl-form-content">
          <form id="universal-product-form" onSubmit={handleSubmit}>
            {/* Basic Information */}
            <div className="upl-form-section">
              <h2 className="upl-form-section-title">Basic Information</h2>
              <div className="upl-form-grid">
                <div className="upl-form-group upl-form-full">
                  <label className="upl-form-label">Product Name <span className="required">*</span></label>
                  <input
                    type="text"
                    className="upl-form-input"
                    value={formData.name}
                    onChange={(e) => updateField('name', e.target.value)}
                    placeholder="e.g., Organic Valley Whole Milk"
                    required
                  />
                </div>
                <div className="upl-form-group">
                  <label className="upl-form-label">Brand</label>
                  <input
                    type="text"
                    className="upl-form-input"
                    value={formData.brand}
                    onChange={(e) => updateField('brand', e.target.value)}
                    placeholder="e.g., Organic Valley"
                  />
                </div>
                <div className="upl-form-group">
                  <label className="upl-form-label">Manufacturer</label>
                  <input
                    type="text"
                    className="upl-form-input"
                    value={formData.manufacturer}
                    onChange={(e) => updateField('manufacturer', e.target.value)}
                    placeholder="e.g., Organic Valley Inc."
                  />
                </div>
                <div className="upl-form-group">
                  <label className="upl-form-label">Category <span className="required">*</span></label>
                  <select
                    className="upl-form-select"
                    value={formData.category}
                    onChange={(e) => updateField('category', e.target.value)}
                    required
                  >
                    <option value="">Select a category</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div className="upl-form-group">
                  <label className="upl-form-label">Subcategory</label>
                  <input
                    type="text"
                    className="upl-form-input"
                    value={formData.subcategory}
                    onChange={(e) => updateField('subcategory', e.target.value)}
                    placeholder="e.g., Milk"
                  />
                </div>
                <div className="upl-form-group">
                  <label className="upl-form-label">Department</label>
                  <input
                    type="text"
                    className="upl-form-input"
                    value={formData.department}
                    onChange={(e) => updateField('department', e.target.value)}
                    placeholder="e.g., Dairy"
                  />
                </div>
                <div className="upl-form-group">
                  <label className="upl-form-label">Country <span className="required">*</span></label>
                  <select
                    className="upl-form-select"
                    value={formData.country}
                    onChange={(e) => updateField('country', e.target.value)}
                    required
                  >
                    {countries.map(c => (
                      <option key={c.code} value={c.code}>{c.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Product Identifiers */}
            <div className="upl-form-section">
              <h2 className="upl-form-section-title">Product Identifiers</h2>
              <div className="upl-form-grid-3">
                <div className="upl-form-group">
                  <label className="upl-form-label">UPC</label>
                  <input
                    type="text"
                    className="upl-form-input"
                    value={formData.upc}
                    onChange={(e) => updateField('upc', e.target.value)}
                    placeholder="e.g., 093966001006"
                  />
                </div>
                <div className="upl-form-group">
                  <label className="upl-form-label">EAN</label>
                  <input
                    type="text"
                    className="upl-form-input"
                    value={formData.ean}
                    onChange={(e) => updateField('ean', e.target.value)}
                    placeholder="e.g., 5012345678900"
                  />
                </div>
                <div className="upl-form-group">
                  <label className="upl-form-label">SKU</label>
                  <input
                    type="text"
                    className="upl-form-input"
                    value={formData.sku}
                    onChange={(e) => updateField('sku', e.target.value)}
                    placeholder="e.g., SKU-12345"
                  />
                </div>
              </div>
            </div>

            {/* Size & Packaging */}
            <div className="upl-form-section">
              <h2 className="upl-form-section-title">Size & Packaging</h2>
              <div className="upl-form-grid">
                <div className="upl-form-group">
                  <label className="upl-form-label">Display Size</label>
                  <input
                    type="text"
                    className="upl-form-input"
                    value={formData.size}
                    onChange={(e) => updateField('size', e.target.value)}
                    placeholder="e.g., 4 L or 64 oz"
                  />
                </div>
                <div className="upl-form-group">
                  <label className="upl-form-label">Pack Size</label>
                  <input
                    type="number"
                    className="upl-form-input"
                    value={formData.packSize}
                    onChange={(e) => updateField('packSize', e.target.value)}
                    placeholder="e.g., 12 for a 12-pack"
                  />
                </div>
                <div className="upl-form-group">
                  <label className="upl-form-label">Size Value</label>
                  <input
                    type="number"
                    step="0.01"
                    className="upl-form-input"
                    value={formData.sizeValue}
                    onChange={(e) => updateField('sizeValue', e.target.value)}
                    placeholder="e.g., 4 or 64"
                  />
                </div>
                <div className="upl-form-group">
                  <label className="upl-form-label">Size Unit</label>
                  <select
                    className="upl-form-select"
                    value={formData.sizeUnit}
                    onChange={(e) => updateField('sizeUnit', e.target.value)}
                  >
                    <option value="">Select unit</option>
                    {SIZE_UNITS.map(unit => (
                      <option key={unit} value={unit}>{unit}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Pricing */}
            <div className="upl-form-section">
              <h2 className="upl-form-section-title">Pricing</h2>
              <div className="upl-form-grid-3">
                <div className="upl-form-group">
                  <label className="upl-form-label">MSRP</label>
                  <input
                    type="number"
                    step="0.01"
                    className="upl-form-input"
                    value={formData.msrp}
                    onChange={(e) => updateField('msrp', e.target.value)}
                    placeholder="e.g., 6.99"
                  />
                </div>
                <div className="upl-form-group">
                  <label className="upl-form-label">Typical Price Range (Min)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="upl-form-input"
                    value={priceRangeMin}
                    onChange={(e) => setPriceRangeMin(e.target.value)}
                    placeholder="e.g., 5.99"
                  />
                </div>
                <div className="upl-form-group">
                  <label className="upl-form-label">Typical Price Range (Max)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="upl-form-input"
                    value={priceRangeMax}
                    onChange={(e) => setPriceRangeMax(e.target.value)}
                    placeholder="e.g., 7.99"
                  />
                </div>
              </div>
            </div>

            {/* Product Images */}
            <div className="upl-form-section">
              <h2 className="upl-form-section-title">Product Images</h2>
              <div className="upl-pill-input-row">
                <input
                  type="text"
                  className="upl-form-input"
                  value={imageUrlInput}
                  onChange={(e) => setImageUrlInput(e.target.value)}
                  placeholder="Enter image URL (e.g., https://example.com/image.jpg)"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addImage();
                    }
                  }}
                />
                <button
                  type="button"
                  className="upl-btn outline"
                  onClick={addImage}
                  disabled={!imageUrlInput.trim() || isUploadingImage}
                >
                  {isUploadingImage ? 'Uploading...' : <><PlusIcon /> Add URL</>}
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleFileUpload}
                />
                <button
                  type="button"
                  className="upl-btn primary"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploadingImage}
                >
                  {isUploadingImage ? 'Uploading...' : 'Upload File'}
                </button>
              </div>

              {formData.images.length > 0 && (
                <div className="upl-image-grid">
                  {formData.images.map((image, index) => (
                    <div key={index} className="upl-image-item">
                      <img src={image.url} alt={`Product ${index + 1}`} />
                      {image.isPrimary && (
                        <span className="upl-image-primary-badge">Primary</span>
                      )}
                      <div className="upl-image-overlay">
                        {!image.isPrimary && (
                          <button
                            type="button"
                            className="set-primary"
                            onClick={() => setPrimaryImage(index)}
                          >
                            Set Primary
                          </button>
                        )}
                        <button
                          type="button"
                          className="remove-bg"
                          onClick={() => removeBackground(index)}
                          disabled={removingBgIndex !== null}
                        >
                          <ScissorsIcon /> {removingBgIndex === index ? 'Removing...' : 'Remove BG'}
                        </button>
                        <button
                          type="button"
                          className="remove"
                          onClick={() => removeImage(index)}
                        >
                          <XIcon />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Tags */}
            <div className="upl-form-section">
              <h2 className="upl-form-section-title">Tags</h2>
              <div className="upl-pill-input-row">
                <input
                  type="text"
                  className="upl-form-input"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                  placeholder="Add a tag (e.g., organic, dairy-free)"
                />
                <button type="button" className="upl-btn outline" onClick={addTag}>
                  <PlusIcon />
                  Add
                </button>
              </div>
              {formData.tags.length > 0 && (
                <div className="upl-pills">
                  {formData.tags.map((tag, i) => (
                    <span key={i} className="upl-pill purple">
                      {tag}
                      <button type="button" onClick={() => removeTag(tag)}><XIcon /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Aliases */}
            <div className="upl-form-section">
              <h2 className="upl-form-section-title">Aliases</h2>
              <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>Alternative names for this product</p>
              <div className="upl-pill-input-row">
                <input
                  type="text"
                  className="upl-form-input"
                  value={aliasInput}
                  onChange={(e) => setAliasInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addAlias())}
                  placeholder="Add an alias (e.g., Coke for Coca-Cola)"
                />
                <button type="button" className="upl-btn outline" onClick={addAlias}>
                  <PlusIcon />
                  Add
                </button>
              </div>
              {formData.aliases.length > 0 && (
                <div className="upl-pills">
                  {formData.aliases.map((alias, i) => (
                    <span key={i} className="upl-pill blue">
                      {alias}
                      <button type="button" onClick={() => removeAlias(alias)}><XIcon /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Keywords */}
            <div className="upl-form-section">
              <h2 className="upl-form-section-title">Search Keywords</h2>
              <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>Additional keywords to improve searchability</p>
              <div className="upl-pill-input-row">
                <input
                  type="text"
                  className="upl-form-input"
                  value={keywordInput}
                  onChange={(e) => setKeywordInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
                  placeholder="Add a keyword"
                />
                <button type="button" className="upl-btn outline" onClick={addKeyword}>
                  <PlusIcon />
                  Add
                </button>
              </div>
              {formData.keywords.length > 0 && (
                <div className="upl-pills">
                  {formData.keywords.map((keyword, i) => (
                    <span key={i} className="upl-pill gray">
                      {keyword}
                      <button type="button" onClick={() => removeKeyword(keyword)}><XIcon /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </form>
        </div>
      </div>

      {/* Footer Action Bar */}
      <div className="action-bar">
        <button type="button" className="action-btn secondary" onClick={onClose}>Cancel</button>
        <button type="submit" form="universal-product-form" className="action-btn primary" disabled={isSaving}>
          {isSaving ? 'Saving...' : (isEditMode ? 'Update' : 'Save')}
        </button>
        {isEditMode && (
          <button
            type="button"
            className="action-btn primary-dark"
            disabled={isSaving}
            onClick={() => {
              closeAfterSaveRef.current = true;
              document.getElementById('universal-product-form').requestSubmit();
            }}
          >
            Update & Close
          </button>
        )}
        {isEditMode && onDelete && (
          <button type="button" className="action-btn danger" onClick={onDelete}>
            Delete
          </button>
        )}
      </div>
    </div>
  );
}
